import { useCallback, useMemo, useRef, useState } from 'react';
import { notifications } from '@/lib/notifications';
import { useTranslation } from '../../../../../i18n/hooks/useTranslation';
import { toBnDigits } from '../../../../../utils/bnFormat';
import { recordToolUsage } from '../../../../../services/toolsService';
import { formatFileSize } from '../compress/compressPdfEngine';
import { convertPdfsToImages, zipImageFiles } from './pdfToImageEngine';
import { applyPdfPageEdits, pagesHaveEdits } from '../shared/pdfPageEditEngine';
import PdfDropZone from '../../components/pdf/PdfDropZone';
import { PdfUploadedFile } from '../../components/pdf/PdfFileRow';
import ToolPageLayout from '../../components/common/ToolPageLayout';
import ToolPreviewPanel from '../../components/common/ToolPreviewPanel';
import PdfDocPreview from '../../components/pdf/PdfDocPreview';
import PdfPageEditor from '../../components/pdf/PdfPageEditor';
import { usePdfPageEdits, fileKeyOf } from '../../components/pdf/usePdfPageEdits';
import ToolReadyHint from '../../components/common/ToolReadyHint';
import ToolCard from '../../components/common/ToolCard';
import OutputFormatPicker from '../../image/shared/OutputFormatPicker';
import Hint from '../../../../../components/common/Hint';
import {
    deriveToolStatus,
    downloadBlob,
    genericHowToSteps,
    getToolStatusText,
} from '../../toolPageUtils';
import { usePdfPaste } from '../../components/common/useFilePaste';

const MAX_FILE_BYTES = 500 * 1024 * 1024;

// The same format ids the image tools' shared picker offers.
const FORMAT_IDS = ['jpg', 'png', 'webp', 'avif'];

// `grid` is how many pixels a side the glyph draws — finer grid, higher DPI.
// Each tile's tooltip is its name plus the DPI line.
const QUALITY_OPTIONS = [
    { id: 'normal', labelKey: 'pdfToImage.quality.normal', descKey: 'pdfToImage.quality.normalDesc', grid: 2 },
    { id: 'high', labelKey: 'pdfToImage.quality.high', descKey: 'pdfToImage.quality.highDesc', grid: 3 },
    { id: 'ultra', labelKey: 'pdfToImage.quality.ultra', descKey: 'pdfToImage.quality.ultraDesc', grid: 4 },
];

/** Tile in an icon tray: glyph over label, same shape as the image tools' trays. */
function tileBtnClass(active) {
    return `flex min-h-[54px] flex-col items-center justify-center gap-1 rounded-lg border-none px-1 py-1.5 text-[11px] leading-tight transition-all ${
        active
            ? 'bg-white font-semibold text-pdf-cat-solid shadow-[0_1px_2px_rgba(28,24,21,0.04)] dark:bg-zinc-900 dark:text-orange-400'
            : 'bg-transparent font-medium text-[#6B635C] dark:text-zinc-400'
    }`;
}

/** Resolution as pixel density: a 20px square split into an N×N grid. */
function ResolutionGlyph({ grid }) {
    const size = 20;
    const gap = 1.5;
    const cell = (size - gap * (grid - 1)) / grid;
    return (
        <svg width={size} height={size} viewBox={`0 0 ${size} ${size}`} aria-hidden="true">
            {Array.from({ length: grid * grid }, (_, i) => (
                <rect
                    // eslint-disable-next-line react/no-array-index-key
                    key={i}
                    x={(i % grid) * (cell + gap)}
                    y={Math.floor(i / grid) * (cell + gap)}
                    width={cell}
                    height={cell}
                    rx={0.8}
                    fill="currentColor"
                    opacity={0.85}
                />
            ))}
        </svg>
    );
}

export default function PdfToImage() {
    const { t } = useTranslation();
    const fileInputRef = useRef(null);

    const [files, setFiles] = useState([]);
    const [format, setFormat] = useState('jpg');
    const [quality, setQuality] = useState('high');
    const [isDragging, setIsDragging] = useState(false);
    const [processing, setProcessing] = useState(false);
    const [progress, setProgress] = useState(null);
    const [result, setResult] = useState(null);
    const [error, setError] = useState(null);
    const [previewIndex, setPreviewIndex] = useState(0);

    const clearResult = useCallback(() => {
        setResult(null);
        setError(null);
        setProgress(null);
    }, []);
    const { pagesByKey, updatePagesForKey, resetPageEdits } = usePdfPageEdits(files, clearResult);

    const addFiles = useCallback((incoming) => {
        setError(null);
        const list = Array.from(incoming || []);
        const pdfs = list.filter((f) => f.type === 'application/pdf' || f.name.toLowerCase().endsWith('.pdf'));
        if (!pdfs.length) {
            const msg = t('pdfToImage.errors.invalidType');
            setError(msg);
            notifications.show({ title: t('common.error'), message: msg, color: 'red' });
            return;
        }

        setFiles((prev) => {
            const next = [...prev];
            for (const file of pdfs) {
                if (file.size > MAX_FILE_BYTES) {
                    const msg = t('pdfToImage.errors.tooLarge');
                    setError(msg);
                    notifications.show({ title: t('common.error'), message: msg, color: 'red' });
                    continue;
                }
                if (next.some((f) => f.name === file.name && f.size === file.size)) continue;
                next.push(file);
            }
            return next;
        });
        setResult(null);
        setError(null);
    }, [t]);

    // Ctrl/Cmd+V anywhere on the page drops a copied PDF straight in.
    usePdfPaste(addFiles);

    const handleDrop = (e) => {
        e.preventDefault();
        setIsDragging(false);
        addFiles(e.dataTransfer.files);
    };

    const removeFile = (index) => {
        setFiles((prev) => prev.filter((_, i) => i !== index));
        setPreviewIndex((prev) => (index < prev ? prev - 1 : Math.max(0, Math.min(prev, files.length - 2))));
        setResult(null);
        setError(null);
    };

    const resetAll = () => {
        setFiles([]);
        setResult(null);
        setProgress(null);
        setError(null);
        setPreviewIndex(0);
        resetPageEdits();
    };

    const handleConvert = async () => {
        if (!files.length || processing) return;

        // Guard: a file whose every page was removed cannot be converted.
        for (const file of files) {
            const pages = pagesByKey[fileKeyOf(file)];
            if (Array.isArray(pages) && pages.length && pages.every((p) => p.removed)) {
                const msg = t('pdfToImage.errors.removeAll');
                setError(msg);
                notifications.show({ title: t('common.error'), message: msg, color: 'red' });
                return;
            }
        }

        recordToolUsage('pdf/pdf-to-image', { category: 'pdf' });

        setProcessing(true);
        setResult(null);
        setError(null);

        try {
            // Apply per-page edits (rotate / crop / delete / reorder / duplicate) before converting.
            const sources = [];
            for (const file of files) {
                const pages = pagesByKey[fileKeyOf(file)];
                if (Array.isArray(pages) && pagesHaveEdits(pages)) {
                    const edited = await applyPdfPageEdits(file, pages);
                    sources.push(new File([edited], file.name, { type: 'application/pdf' }));
                } else {
                    sources.push(file);
                }
            }

            const images = await convertPdfsToImages(
                sources,
                { format, quality },
                (page, total) => setProgress({ page, total }),
            );

            if (images.length === 1) {
                setResult({
                    kind: 'single',
                    name: images[0].name,
                    blob: images[0].blob,
                    count: 1,
                });
            } else {
                const zipBlob = await zipImageFiles(images);
                const base = files[0].name.replace(/\.pdf$/i, '');
                setResult({
                    kind: 'zip',
                    name: `${base}-images.zip`,
                    blob: zipBlob,
                    count: images.length,
                });
            }

            notifications.show({ title: t('common.success'), message: t('pdfToImage.done'), color: 'green' });
        } catch (err) {
            let msg = t('pdfToImage.errors.failed');
            if (err?.message === 'password') msg = t('pdfToImage.errors.password');
            else if (err?.message === 'removeAll') msg = t('pdfToImage.errors.removeAll');
            setError(msg);
            notifications.show({ title: t('common.error'), message: msg, color: 'red' });
        } finally {
            setProcessing(false);
            setProgress(null);
        }
    };

    const progressLabel = progress
        ? t('pdfToImage.progress', {
            page: toBnDigits(progress.page),
            pages: toBnDigits(progress.total),
        })
        : '';

    const totalInputSize = files.reduce((sum, f) => sum + f.size, 0);

    const status = deriveToolStatus({
        hasInput: files.length > 0,
        canRun: files.length > 0,
        processing,
        hasResult: !!result,
        hasError: !!error,
    });

    const howToSteps = useMemo(() => genericHowToSteps(t), [t]);

    const resultMeta = result
        ? (result.kind === 'zip'
            ? t('pdfToImage.resultZip', { count: toBnDigits(result.count), size: formatFileSize(result.blob.size) })
            : t('pdfToImage.resultSingle', { size: formatFileSize(result.blob.size) }))
        : undefined;
    const progressPercent = progress?.total
        ? Math.round((progress.page / progress.total) * 100)
        : undefined;
    const statusText = getToolStatusText(status, t, {
        success: t('pdfToImage.done'),
        error: error || t('toolPage.statusText.error'),
    });
    const fileRows = files.map((selectedFile, index) => (
        <PdfUploadedFile
            key={`${selectedFile.name}-${selectedFile.size}`}
            name={selectedFile.name}
            meta={formatFileSize(selectedFile.size)}
            selected={previewIndex === index}
            onSelect={() => setPreviewIndex(index)}
            onRemove={() => removeFile(index)}
            removeLabel={t('pdfToImage.removeFile')}
        />
    ));

    return (
        <ToolPageLayout
            slug="pdf/pdf-to-image"
            subtitleKey="pdfToImage.subtitle"
            status={status}
            statusText={statusText}
            controlsTitle={t('toolPage.files', { count: toBnDigits(files.length) })}
            showAdd={files.length > 0}
            onAdd={() => fileInputRef.current?.click()}
            howToSteps={howToSteps}
            onReset={files.length > 0 || result || error ? resetAll : undefined}
            primaryAction={{
                label: t('pdfToImage.convert'),
                onClick: handleConvert,
                disabled: !files.length || processing || !!result,
                loading: processing,
            }}
            controls={(
                <>
                    <input
                        ref={fileInputRef}
                        type="file"
                        accept="application/pdf,.pdf"
                        multiple
                        className="hidden"
                        onChange={(e) => { addFiles(e.target.files); e.target.value = ''; }}
                    />

                    {files.length === 0 ? (
                        <PdfDropZone
                            title={t('pdfToImage.dropTitle')}
                            hint={t('pdfToImage.dropHint')}
                            isDragging={isDragging}
                            onClick={() => fileInputRef.current?.click()}
                            onKeyDown={(e) => e.key === 'Enter' && fileInputRef.current?.click()}
                            onDragOver={(e) => { e.preventDefault(); setIsDragging(true); }}
                            onDragLeave={() => setIsDragging(false)}
                            onDrop={handleDrop}
                        />
                    ) : (
                        <div className="space-y-2">
                            <div className="max-h-[220px] space-y-2 overflow-y-auto pr-1 [scrollbar-width:thin]">
                                {fileRows}
                            </div>
                            <p className="text-xs text-zinc-400">
                                {t('pdfToImage.fileCount', { count: toBnDigits(files.length), size: formatFileSize(totalInputSize) })}
                            </p>
                        </div>
                    )}
                </>
            )}
            settings={(
                <>
                    {/* The image tools' own format picker — same icons, same tiles,
                        AVIF written in WebAssembly where the browser cannot. */}
                    <OutputFormatPicker
                        value={format}
                        onChange={(next) => { setFormat(next); setResult(null); setError(null); }}
                        formats={FORMAT_IDS}
                        disabled={processing}
                    />

                    <ToolCard title={t('pdfToImage.qualityTitle')}>
                        {/* Tiles like the image tools: density glyph over the name, the
                            DPI detail in the tooltip and on the line below. */}
                        <div className="grid grid-cols-3 gap-1 rounded-xl bg-[#F7F5F3] p-1 dark:bg-zinc-800">
                            {QUALITY_OPTIONS.map((opt) => (
                                // The app's tooltip, not the browser's `title`. The wrapper keeps it
                                // working while the tiles are disabled (a disabled button swallows hover).
                                <Hint key={opt.id} label={`${t(opt.labelKey)} — ${t(opt.descKey)}`}>
                                    <span className="flex min-w-0 [&>button]:w-full">
                                        <button
                                            type="button"
                                            onClick={() => { setQuality(opt.id); setResult(null); setError(null); }}
                                            aria-pressed={quality === opt.id}
                                            disabled={processing}
                                            className={tileBtnClass(quality === opt.id)}
                                        >
                                            <ResolutionGlyph grid={opt.grid} />
                                            {t(opt.labelKey)}
                                        </button>
                                    </span>
                                </Hint>
                            ))}
                        </div>
                        <p className="mt-2 text-[11.5px] leading-relaxed text-[#A8A099] dark:text-zinc-500">
                            {t(QUALITY_OPTIONS.find((opt) => opt.id === quality)?.descKey || 'pdfToImage.quality.highDesc')}
                        </p>
                    </ToolCard>
                </>
            )}
            preview={(
                <ToolPreviewPanel
                    title={t('toolPage.previewTitle')}
                    status={status}
                    statusLabel={t(`toolPage.status.${status}`)}
                    idleTitle={t('toolPage.previewEmpty')}
                    idleHint={t('toolPage.previewEmptyHint')}
                    processingLabel={progressLabel || t('pdfToImage.converting')}
                    progress={progressPercent}
                    error={error}
                    successLabel={resultMeta}
                    downloadLabel={t('pdfToImage.download')}
                    onDownload={result ? () => downloadBlob(result.blob, result.name) : undefined}
                    onEditAgain={result ? clearResult : undefined}
                    editAgainLabel={t('toolPage.editAgain')}
                    onAgain={result ? resetAll : undefined}
                    againLabel={t('pdfToImage.startOver')}
                    readyHint={status === 'ready' ? <ToolReadyHint actionLabel={t('pdfToImage.convert')} /> : null}
                    onIdleClick={() => fileInputRef.current?.click()}
                    onIdleDrop={handleDrop}
                    onIdleDragOver={(e) => { e.preventDefault(); setIsDragging(true); }}
                    onIdleDragLeave={() => setIsDragging(false)}
                    idleDragging={isDragging}
                    showIdle={files.length === 0 && !error}
                    pdfPreview={files.length > 0 && !processing && !error}
                >
                    {files.length > 0 && !processing && !error ? (
                        <PdfPageEditor
                            files={files}
                            pagesByKey={pagesByKey}
                            onPagesChangeForKey={updatePagesForKey}
                            activeIndex={previewIndex}
                            onActiveIndexChange={setPreviewIndex}
                        />
                    ) : null}
                </ToolPreviewPanel>
            )}
        />
    );
}
